import { CartLinesUpdateEvent } from '@shopify/events';

// "You may also like" strip in the cart drawer. Fetches related products from the
// Section Rendering API and adds them through the same CartLinesUpdateEvent the
// product form uses, so the drawer and cart bubble refresh themselves.
class CartUpsell extends HTMLElement {
  #loading = false;
  #observer = new MutationObserver(() => {
    // The drawer re-morphs from server HTML (empty + hidden) after every cart change: refill it.
    if (this.hidden || !this.querySelector('.cart-upsell__list')) this.#load();
  });

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer.disconnect();
  }

  #load() {
    const id = this.dataset.productId;
    if (!id || this.#loading) return;
    this.#loading = true;
    const root = window.Shopify?.routes?.root ?? '/';
    fetch(`${root}recommendations/products?section_id=cart-upsell&product_id=${id}&limit=6&intent=related`)
      .then((r) => r.text())
      .then((html) => {
        const list = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-upsell__list');
        if (!list?.children.length) return;
        this.querySelector('[data-upsell-list]')?.replaceChildren(list);
        this.hidden = false;
      })
      .catch(() => {})
      .finally(() => {
        this.#loading = false;
      });
  }

  #onClick = async (event) => {
    const dir = event.target.closest('[data-upsell-prev]') ? -1 : event.target.closest('[data-upsell-next]') ? 1 : 0;
    if (dir) {
      const list = this.querySelector('.cart-upsell__list');
      list?.scrollBy({ left: dir * list.clientWidth, behavior: 'smooth' });
      return;
    }
    const btn = event.target.closest('[data-upsell-add]');
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    btn.textContent = '…';
    const root = window.Shopify?.routes?.root ?? '/';
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: btn.dataset.upsellAdd, quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(btn.dataset.upsellAdd),
          quantity: 1,
          sections: 'cart-drawer-section',
          sections_url: window.location.pathname,
        }),
      });
      const added = await res.json();
      if (added.status) throw new Error(added.description || added.message);
      const cart = await (await fetch(`${root}cart.js`)).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-upsell',
          sourceId: 'cart-upsell',
          itemCount: 1,
          productId: btn.dataset.productId,
          sections: added.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      btn.disabled = false;
      btn.textContent = 'Add';
    }
  };
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
